// 批次 24（#26，Gitea issue #26）：course-course-static-list（開課統計）complexBody 整頁置換。
// origin course/courseStaticList.jsp 395 行（CourseController.java:543 toCourseStaticPage／:563-566 queryCourseStaticListAjax）。
//
//   - 查詢表單 8 欄（:302-360）：學年度 annualId（required）／醫院 organizationId（required）／
//     職稱 jobTypeId／科部 departmentId（required）／類型 kind（全部/課程/會議）／
//     課程類別 caseTypeDisplay／課程日期 queryStartDateS・queryStartDateE（required）＋
//     resetDateTimePicker 清除鈕（:365）。
//   - doQuery（:142-235）：驗證鏉（:153-164：醫院＋課程日期 → '請選擇'+逗號清單，
//     **學年度/科部不檢**）→ POST course/queryCourseStaticListAjax（:169，server 端過濾）→
//     成功 DataTable rows.add(list)（:199）；失敗 showAlertDialog(response.desc)＋
//     currentTable.destroy()（:226-230）。
//   - 統計表 6 欄（:44-96）：課程/會議(kind)／類型(courseTypeName)／月份(courseYM)／
//     部門(departmentName)／場次(courseNums，null→'0')／總時數(courseHourNum，null→'0')；
//     無列互動（:44-96 無 tbody click）。
//
// 誠實標註（KNOWN-NOT-DONE D24-x，詳 src/ui/docs/KNOWN-NOT-DONE.md）：
//   - D24-1：圓餅圖 2 面（canvas :382／canvas2 :385，Chart.js pie：課程類別開課場次統計
//     :240-258／部門開課場次統計 :262-282，依 courseTypeName/departmentName 場次加總百分比
//     :179-216 toFixed(2)）未鏡像＝B14-1 同款，併 parity #34 RPT-001 追蹤。
//   - D24-2：組織級聯（initSearchForm :33-41 setOrganizationElement 預選帳號醫院＋trigger change →
//     organizationChangeEvent :99-129 setAllJobTypeElement/setAllDepartmentElementByOrganization
//     per-org 選項重載＋快取 jobTypeOptionFull/allDepartmentOptionFull）未鏡像——選項池靜態；
//     jobTypeId 鏡像 prepend -2 全部（:112，20210129 #0004203）＋預選 -2。
//   - D24-3：server 失敗 currentTable.destroy()（:226-230，DataTable 特徵销毀）未鏡像——
//     setRows([]) 等價清空。
//   - D24-4：setStudentList($('#searchStudentCode'), $('#studentId'))（:40）＝頁面無此元素 no-op，
//     照實不實作。
//   - 預選值【雛形值待實機校準】：年度 raw-dom 預選 115（isDefault==1）；醫院池首
//     （legacy setOrganizationElement 回呼預選帳號醫院 :36-38）；科部＝請選擇
//     （setAllDepartmentElementByOrganization 第 4 參 null（:123-126）→ ePortfolio.js:577-614
//     無 selected＝首 option 請選擇(-1)，raw-dom rendered=請選擇）；annualDisp==0 中華民國年
//     顯示未鏡像（:302-306 myAccount.myJobType.annualDisp）。
//   - 查詢為伺服器端過濾（queryCourseStaticListAjax，CourseController.java:566 ClassInCourse
//     queryCriteria）——雛形 filters 直送 MockDataSource＋QUERY_PASSTHROUGH_RESOURCES 種子直出
//     （批次 23 先例），過濾語意【雛形值待實機校準】。
//   - 送出日期 epdateutil/datepicker → 種子直接存 YYYY-MM-DD。
//   - resp.desc 成功/失敗文案為 server 回應【雛形值待實機校準】→ 查詢失敗走 legacyAlertFallback。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useState, type ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { PieCanvas, chartColorAt } from '@/components/base/ChartCanvas';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ContentPanel, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import { useDataSource } from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import type { Row } from '@/mocks/types';
import {
  ACCOUNT_DEPARTMENT_OPTIONS,
  ACCOUNT_JOB_TYPE_OPTIONS,
  ACCOUNT_ORGANIZATION_OPTIONS,
} from '@/mocks/resource-rules';
import { legacyAlertFallback } from '../templates/page-behaviors';
import { toUiKey } from '../templates/shared';

/** 學年度選項（annualList；raw-dom 預選 115＝isDefault；annualDisp 顯示未鏡像＝D24-2 同族）。 */
const COURSE_STATIC_ANNUAL_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '114', label: '114' },
  { value: '115', label: '115' },
];

/** 類型選項（kind select :332-337：全部＋kindList EnumConfigType.KIND forEach；
 *  課程/會議值域權威＝legacy-fields.generated.ts:144 static【雛形值待實機校準】）。 */
const COURSE_STATIC_KIND_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'class', label: '課程' },
  { value: 'meeting', label: '會議' },
];

/** 職稱選項（organizationChangeEvent :99-129：per-org 重載＋prepend -2 全部 :112＝靜態鏡像）。 */
const COURSE_STATIC_JOB_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '-2', label: '全部' },
  ...ACCOUNT_JOB_TYPE_OPTIONS,
];

export function CourseStaticListBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  // 批次 52 種子值校準：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數
  // （規格 .tmp/tasks/orca-tasks/b52-seed-spec.md §7.1；placeholder 同步移除——凍結首項
  // 已是 prompt 等值選項，如 departmentId 首項＝請選擇(-1)、jobTypeId 首項＝全部(-2)）。
  const overrideOptions = (name: string) => selectOptionsOverride(pageId, name);

  // 查詢表單值（預選：年度 115＋醫院池首【雛形值待實機校準】；職稱 -2 全部＝:112 預選鏡像；
  // 科部＝請選擇（setAllDepartmentElementByOrganization 第 4 參 null（:123-126）→無 selected＝
  // 首 option 請選擇(-1)，ePortfolio.js:577-614；raw-dom rendered=請選擇）。
  const [annualId, setAnnualId] = useState('115');
  const [organizationId, setOrganizationId] = useState(ACCOUNT_ORGANIZATION_OPTIONS[0]!.value);
  const [jobTypeId, setJobTypeId] = useState('-2');
  const [departmentId, setDepartmentId] = useState('');
  const [kind, setKind] = useState('');
  const [caseTypeDisplay, setCaseTypeDisplay] = useState('');
  const [queryStartDateS, setQueryStartDateS] = useState('');
  const [queryStartDateE, setQueryStartDateE] = useState('');
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [busy, setBusy] = useState(false);

  async function doQuery(): Promise<void> {
    // 驗證鏉（courseStaticList.jsp:153-164）：**只有醫院＋課程日期**（學年度/科部不檢）。
    const missing: string[] = [];
    if (organizationId === '') {
      missing.push('醫院');
    }
    if (queryStartDateS === '' || queryStartDateE === '') {
      missing.push('課程日期');
    }
    if (missing.length > 0) {
      await dialogs.alert(`請選擇${missing.join(',')}`);
      return;
    }
    setBusy(true);
    try {
      // course/queryCourseStaticListAjax（:169；server 端過濾＝ClassInCourse queryCriteria）。
      const result = await dataSource.list(pageId, {
        page: 1,
        pageSize: 50,
        filters: {
          annualId,
          organizationId,
          jobTypeId,
          departmentId,
          kind,
          caseTypeDisplay,
          queryStartDateS,
          queryStartDateE,
        },
      });
      setRows(result.rows);
    } catch (cause: unknown) {
      // 失敗（:226-230）：showAlertDialog(response.desc)＋currentTable.destroy() → 清空（D24-3）。
      setRows([]);
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  const columns: readonly TableColumn[] = [
    { key: 'kind', header: '課程/會議', align: 'center' },
    { key: 'courseTypeName', header: '類型', align: 'center' },
    { key: 'courseYM', header: '月份', align: 'center' },
    { key: 'departmentName', header: '部門', align: 'center' },
    { key: 'courseNums', header: '場次', align: 'center' },
    { key: 'courseHourNum', header: '總時數', align: 'center' },
  ];

  return (
    <div data-ui-id={pageId}>
      <ContentPanel uiId={`${pageId}-panel`} title="開課統計" headingLevel={panelHeadingLevelFor(pageId)}>
        {/* 查詢表單（courseStaticList.jsp:298-374；qryLabel/qryField 欄位群）。 */}
        <div data-ui-id={`${pageId}-query`} className="mb-4 grid grid-cols-2 gap-2">
          {QueryField({
            pageId,
            keyName: 'annualId',
            label: '學年度：',
            required: true,
            control: (
              <Select
                uiId={`${pageId}-query-annual-id`}
                name="annualId"
                value={annualId}
                onChange={setAnnualId}
                options={overrideOptions('annualId') ?? COURSE_STATIC_ANNUAL_OPTIONS}
                {...(overrideOptions('annualId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            keyName: 'organizationId',
            label: '醫院：',
            required: true,
            control: (
              <Select
                uiId={`${pageId}-query-organization-id`}
                name="organizationId"
                value={organizationId}
                onChange={setOrganizationId}
                options={overrideOptions('organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS}
                {...(overrideOptions('organizationId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            keyName: 'jobTypeId',
            label: '訓練類別：',
            required: false,
            control: (
              <Select
                uiId={`${pageId}-query-job-type-id`}
                name="jobTypeId"
                value={jobTypeId}
                onChange={setJobTypeId}
                options={overrideOptions('jobTypeId') ?? COURSE_STATIC_JOB_TYPE_OPTIONS}
                {...(overrideOptions('jobTypeId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            keyName: 'departmentId',
            // JSP 標籤無紅星號（courseStaticList.jsp:324-326，僅 select required attr）。
            label: '科部：',
            required: false,
            control: (
              <Select
                uiId={`${pageId}-query-department-id`}
                name="departmentId"
                value={departmentId}
                onChange={setDepartmentId}
                options={overrideOptions('departmentId') ?? ACCOUNT_DEPARTMENT_OPTIONS}
                {...(overrideOptions('departmentId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            keyName: 'kind',
            label: '類型：',
            required: false,
            control: (
              <Select
                uiId={`${pageId}-query-kind`}
                name="kind"
                value={kind}
                onChange={setKind}
                options={overrideOptions('kind') ?? COURSE_STATIC_KIND_OPTIONS}
              />
            ),
          })}
          {QueryField({
            pageId,
            keyName: 'caseTypeDisplay',
            label: '課程類別：',
            required: false,
            control: (
              <TextInput
                uiId={`${pageId}-query-case-type-display`}
                name="caseTypeDisplay"
                value={caseTypeDisplay}
                onChange={setCaseTypeDisplay}
              />
            ),
          })}
          {QueryField({
            pageId,
            keyName: 'queryStartDateS',
            label: '課程日期起：',
            required: true,
            control: (
              <TextInput
                uiId={`${pageId}-query-query-start-date-s`}
                name="queryStartDateS"
                value={queryStartDateS}
                onChange={setQueryStartDateS}
                maxLength={10}
                placeholder="YYYY-MM-DD"
              />
            ),
          })}
          {QueryField({
            pageId,
            keyName: 'queryStartDateE',
            label: '課程日期迄：',
            required: true,
            control: (
              <div className="flex items-center gap-2">
                <TextInput
                  uiId={`${pageId}-query-query-start-date-e`}
                  name="queryStartDateE"
                  value={queryStartDateE}
                  onChange={setQueryStartDateE}
                  maxLength={10}
                  placeholder="YYYY-MM-DD"
                />
                {/* 橡皮擦（:365 resetDateTimePicker；input-group-addon 內 fa-eraser 鏡像＝清除兩個日期）。 */}
                <Button
                  uiId={`${pageId}-query-reset-dates`}
                  variant="default"
                  size="sm"
                  type="button"
                  onClick={() => {
                    setQueryStartDateS('');
                    setQueryStartDateE('');
                  }}
                >
                  清除
                </Button>
              </div>
            ),
          })}
          <div className="col-span-2 flex gap-2">
            <Button uiId={`${pageId}-query-btn`} variant="primary" size="sm" type="button" disabled={busy} onClick={() => void doQuery()}>
              查詢
            </Button>
          </div>
        </div>
        {/* RPT-001（批次 33，issue #34）：pie×2（courseStaticList.jsp drawPie :240-259／drawPie2 :261-282；
            canvas :382／canvas2 :385；圖在表格前＝共通事實 6）。資料＝rows 加總（:183-197
            classStatics[classTypeName]／classStatics2[departmentName] 依 courseNums 加總）→
            rate.toFixed(2)（:200-206）；**色 quirk 照實**：drawPie2 的 backgroundColor 引用
            `dataColor`（第一面的顏色陣列）而非 dataColor2（:267）——兩面同色序；種子情境兩面等長（類別 4／部門 4）等價，若資料不等長 legacy 超界部分為 Chart.js 預設灰。 */}
        <div style={{ width: '75%' }}>
          {(() => {
            const sum = (key: 'courseTypeName' | 'departmentName'): readonly { label: string; value: number }[] => {
              const acc = new Map<string, number>();
              let total = 0;
              for (const row of rows) {
                const k = String(row[key] ?? '');
                const n = Number(row['courseNums'] ?? 0);
                acc.set(k, (acc.get(k) ?? 0) + n);
                total += n;
              }
              return [...acc.entries()].map(([label, n]) => ({
                label,
                value: Number(((n / (total || 1)) * 100).toFixed(2)),
              }));
            };
            const byType = sum('courseTypeName');
            const byDept = sum('departmentName');
            return (
              <>
                <PieCanvas
                  uiId={`${pageId}-canvas`}
                  title="課程類別開課場次統計"
                  slices={byType.map((s, i) => ({ ...s, color: chartColorAt(i) }))}
                />
                <PieCanvas
                  uiId={`${pageId}-canvas2`}
                  title="部門開課場次統計"
                  slices={byDept.map((s, i) => ({ ...s, color: chartColorAt(i) }))}
                />
              </>
            );
          })()}
        </div>
        {/* 統計表（欄定義 courseStaticList.jsp:44-96；6 欄；無列互動）。 */}
        <Table
          uiId={`${pageId}-result-table`}
          columns={columns}
          rows={rows}
          rowKey={(_row, index) => `static-row-${String(index)}`}
          emptyText={t('table.empty')}
          renderCell={(row, columnKey) => {
            // 場次/總時數（:73-95）：undefined/null→'0'；其餘（:45-71）undefined/null→''。
            if (columnKey === 'courseNums' || columnKey === 'courseHourNum') {
              const value = row[columnKey];
              return value === undefined || value === null ? '0' : String(value);
            }
            return String(row[columnKey] ?? '');
          }}
        />
        {/* 圓餅圖 2 面（canvas :382／canvas2 :385）未鏡像＝D24-1（B14-1 同款）。 */}
      </ContentPanel>
      <ProcessingOverlay uiId={`${pageId}-processing`} open={busy} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 查詢欄（FormQueryBodies QueryField 同款；批次 23 先例）
// ---------------------------------------------------------------------------

function QueryField({
  pageId,
  keyName,
  label,
  required,
  control,
}: {
  pageId: string;
  keyName: string;
  label: string;
  required: boolean;
  control: ReactElement;
}) {
  return (
    <div className="flex items-center gap-2">
      {/* 批次 50（工作項 4）：htmlFor 字尾 toUiKey kebab 化，與呼叫端 kebab 字面 uiId 成對
          （keyName 本身是 name/payload key 域，參數值不動）。 */}
      <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-query-${toUiKey(keyName)}`}>
        {required ? <span className="text-danger">*</span> : null}
        {label}
      </label>
      {control}
    </div>
  );
}
